import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { Auth } from 'aws-amplify'
import { startCase, toLower } from 'lodash'
import { OrganizationResponseSchema, YardResponseSchema } from '../../gen/rtk-openapi.ts'

export const USER_SELECTED_YARD_LOCAL_STORAGE_KEY = 'userSelectedYardLocalStorage'
export const USER_SELECTED_YARD_ID_LOCAL_STORAGE_KEY = 'userSelectedYardIDLocalStorage'

import { isProduction } from 'utils/envs'
import { LOCALE, LOCALE_MAP } from 'utils/constants'
import importLocaleForDayjs from 'utils/importLocaleForDayjs'

export const clearLocalOrgInfo = () => {
  window.localStorage.removeItem(USER_SELECTED_YARD_LOCAL_STORAGE_KEY)
  window.localStorage.removeItem(USER_SELECTED_YARD_ID_LOCAL_STORAGE_KEY)
}

interface GSCognitoUser {
  attributes: {
    email: string
    email_verified: boolean
    given_name: string
    family_name: string
    phone_number: string
    sub: string
    'custom:org': string
    'custom:yards': string
    'custom:orgModeEnabled': 'true' | 'false'
  }
}

// todo add more specific typing
interface AuthState {
  token: string | null
  organization: string | null
  yards: YardResponseSchema[] | null
  yardId: string | null
  user: GSCognitoUser | null
  selectedYard: string | null
  organizationDetails: OrganizationResponseSchema | null
  yardDetailsList: YardResponseSchema[] | null
  defaultYardTimezone: string | null
  locale: LOCALE
}

const initialState: AuthState = {
  token: null,
  organization: null,
  yards: null,
  yardId: null,
  user: null,
  selectedYard: null,
  organizationDetails: null,
  yardDetailsList: null,
  defaultYardTimezone: null,
  locale: LOCALE.enUS,
}

export const transformYardList = (organization, yards) =>
  yards.map(yard => ({
    id: yard?.id,
    name: startCase(toLower(`${organization} ${yard?.name}`)),
  }))

export const setCurrentAuthToken = createAsyncThunk('auth/setCurrentAuthToken', async () => {
  const session = await Auth.currentSession()
  const idToken = session?.getIdToken()
  const { payload } = idToken || {}

  // if user is portal user, then redirect to customer portal
  if (payload?.['cognito:groups']?.[0] === 'portalUser') {
    const firstPortalOrg = payload?.['custom:portalOrgs']?.split(',')[0]
    window.location.href = `https://portal.greenspark${isProduction ? 'software' : '-dev'}.com/${firstPortalOrg}`
  }

  const yards = payload['custom:yards']?.replaceAll(' ', '').split(',')
  // we want to remember which yard they used last
  const selectedYard = window.localStorage.getItem(USER_SELECTED_YARD_LOCAL_STORAGE_KEY) || yards[0]
  const yardId = window.localStorage.getItem(USER_SELECTED_YARD_ID_LOCAL_STORAGE_KEY) || null

  return {
    token: idToken.getJwtToken(),
    organization: payload['custom:org'],
    yards,
    selectedYard,
    yardId,
  }
})

// TODO this doesn't work for some reason, fix it up later
export const setUserInfo = createAsyncThunk('auth/setUserInfo', async () => {
  const currentUserInfo = await Auth.currentUserInfo()

  return {
    organization: currentUserInfo.attributes['custom:org'],
    yards: currentUserInfo.attributes['custom:yards'],
  }
})

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    // standard reducer logic, with auto-generated action types per reducer
    setAuthToken: (state, action) => {
      state.token = action.payload.token
    },
    clearAuthToken: state => {
      state.token = null
    },
    setYardId: (state, action) => {
      state.yardId = action.payload.yardId
    },
    setUser: (state, action) => {
      state.user = {
        attributes: action.payload.userAttributes,
      }
    },
    setDefaultYardTimezone: (state, action) => {
      state.defaultYardTimezone = action.payload.defaultYardTimezone
    },
    setOrgAndYardsDetails: (state, action) => {
      const { organizationDetails, yardDetailsList } = action.payload

      state.organizationDetails = organizationDetails
      state.yardDetailsList = yardDetailsList
      state.yardId = yardDetailsList?.find(yard => yard?.name === state.selectedYard)?.id
      state.defaultYardTimezone = yardDetailsList?.find(yard => yard?.name === state.selectedYard)?.defaultTimezone
      const yardLocale = yardDetailsList?.find(yard => yard?.name === state.selectedYard)?.locale || LOCALE.enUS
      state.locale = yardLocale
      importLocaleForDayjs(LOCALE_MAP[yardLocale]?.dayjs)
    },
  },
  extraReducers: builder => {
    // Add reducers for additional action types here, and handle loading state as needed
    builder
      .addCase(setCurrentAuthToken.fulfilled, (state, action) => {
        const { token, organization, yards, selectedYard, yardId } = action.payload
        state.token = token
        state.organization = organization
        state.yards = yards
        state.selectedYard = selectedYard
        state.yardId = yardId
      })
      .addCase(setUserInfo.fulfilled, (state, action) => {
        state.organization = action.payload.organization
        state.yards = action.payload.yards
      })
  },
})

// Action creators are generated for each case reducer function
export const { setAuthToken, clearAuthToken, setYardId, setUser, setOrgAndYardsDetails, setDefaultYardTimezone } =
  authSlice.actions

export default authSlice.reducer
